import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { ProductionCardDyeingCycle } from '@app/models/dyeing-dashboard/production-card-dyeing-cycle';
import { LotItem, Requisition } from '@app/models/inventory/requisition';
import { ProductRecipe } from '@app/models/item/product-recipe';
import { Batch, ClientPOAllocation } from '@app/models/production/batch/batch';
import { ENTITY_DEF_BATCH } from '@app/models/production/batch/batch.constants';
import { ProductionWorkOrder, ProductionWorkOrderReservation } from '@app/models/production/production-work-order';
import { BatchLotItemProxyService } from '@app/proxies/inventory/batch-lot-item.proxy.service';
import { RequisitionProxyService } from '@app/proxies/inventory/requisition.proxy.service';
import { BatchProxyService } from '@app/proxies/production/batch.proxy.service';
import { ProductionCardDyeingCycleProxyService } from '@app/proxies/production/production-card-dyein-cycle.proxy.service';
import { ProductionWorkOrderProxyService } from '@app/proxies/production/production-work-order.proxy.service';

@Injectable({
  providedIn: 'root',
})
export class BatchService {
  private readonly batchProxyService = inject(BatchProxyService);
  private readonly batchLotItemProxyService = inject(BatchLotItemProxyService);
  private readonly dyeingCycleProxyService = inject(ProductionCardDyeingCycleProxyService);
  private readonly pwoProxyService = inject(ProductionWorkOrderProxyService);
  private readonly requisitionProxyService = inject(RequisitionProxyService);

  private readonly _batch = signal<Batch | null>(null);
  private readonly _dyeingCycles = signal<ProductionCardDyeingCycle[]>([]);
  private readonly _lotItems = signal<LotItem[]>([]);
  private readonly _productionWorkOrder = signal<ProductionWorkOrder | null>(null);
  private readonly _requisitions = signal<Requisition[]>([]);

  readonly batch = this._batch.asReadonly();
  readonly dyeingCycles = this._dyeingCycles.asReadonly();
  readonly lotItems = this._lotItems.asReadonly();
  readonly productionWorkOrder = this._productionWorkOrder.asReadonly();
  readonly requisitions = this._requisitions.asReadonly();

  private _isInitialized = false;
  private _currentBatchId: string | null = null;

  readonly productRecipe = computed<ProductRecipe | null>(() => {
    return this._batch()?.productRecipe ?? null;
  });

  readonly clientPOAllocations = computed<ClientPOAllocation[]>(() => {
    const pwo = this._productionWorkOrder();
    const batch = this._batch();
    if (!pwo || !batch) return [];

    const matchingItem = pwo.items.find((item) => item.productRecipeNumber === batch.productDevelopmentNumber);
    if (!matchingItem) return [];

    return matchingItem.reservations.map(
      (res: ProductionWorkOrderReservation): ClientPOAllocation => ({
        clientPurchaseOrderReference: res.clientPurchaseOrderReference,
        clientPurchaseOrderNumber: res.clientPurchaseOrderNumber,
        clientPurchaseOrderProductRecipeNumber: res.clientPurchaseOrderProductRecipeNumber,
        reservedQuantity: res.reservedQuantity,
      }),
    );
  });

  initialize(batchId: string): Observable<Batch> {
    const cached = this._batch();
    if (this._isInitialized && this._currentBatchId === batchId && cached) {
      return of(cached);
    }

    this.reset();
    this._currentBatchId = batchId;

    return this.batchProxyService.getById(batchId).pipe(
      switchMap((response) => {
        if (!response.success || !response.data) {
          return throwError(() => new Error('Batch not found'));
        }

        const batch = response.data;
        this._batch.set(batch);

        const entityKey = {
          reference: batch.id,
          entityDefinitionCode: ENTITY_DEF_BATCH,
        };

        // Each companion call degrades to null so one failing section does not block the whole screen.
        return forkJoin({
          pwo: this.pwoProxyService.getById(batch.initiator.reference).pipe(catchError(() => of(null))),
          requisitions: this.requisitionProxyService.getByEntityKey(entityKey, true, true).pipe(catchError(() => of(null))),
          dyeingCycles: this.dyeingCycleProxyService.getByInitiator(batch.id, ENTITY_DEF_BATCH).pipe(catchError(() => of(null))),
          lotItems: this.batchLotItemProxyService.getLotItemsByBatch(batch.id).pipe(catchError(() => of(null))),
        }).pipe(
          map(({ pwo, requisitions, dyeingCycles, lotItems }) => {
            if (pwo?.success && pwo.data) {
              this._productionWorkOrder.set(pwo.data);
            }
            if (requisitions?.success && requisitions.data) {
              this._requisitions.set(requisitions.data);
            }
            if (dyeingCycles?.success && dyeingCycles.data) {
              this._dyeingCycles.set(dyeingCycles.data);
            }
            if (lotItems?.success && lotItems.data) {
              this._lotItems.set(lotItems.data);
            }
            this._isInitialized = true;
            return batch;
          }),
        );
      }),
      catchError((error) => {
        this.reset();
        return throwError(() => error);
      }),
    );
  }

  reloadBatch(batchId: string): Observable<Batch | null> {
    return this.batchProxyService.getById(batchId).pipe(
      tap((response) => {
        if (response.success && response.data) {
          this._batch.set(response.data);
        }
      }),
      map((response) => response.data ?? null),
    );
  }

  reloadDyeingCycles(batchId: string): Observable<ProductionCardDyeingCycle[]> {
    return this.dyeingCycleProxyService.getByInitiator(batchId, ENTITY_DEF_BATCH).pipe(
      tap((response) => {
        if (response.success && response.data) {
          this._dyeingCycles.set(response.data);
        }
      }),
      map((response) => response.data ?? []),
    );
  }

  reloadLotItems(batchId: string): Observable<LotItem[]> {
    return this.batchLotItemProxyService.getLotItemsByBatch(batchId).pipe(
      tap((response) => {
        if (response.success && response.data) {
          this._lotItems.set(response.data);
        }
      }),
      map((response) => response.data ?? []),
    );
  }

  private reset(): void {
    this._batch.set(null);
    this._productionWorkOrder.set(null);
    this._requisitions.set([]);
    this._dyeingCycles.set([]);
    this._lotItems.set([]);
    this._isInitialized = false;
    this._currentBatchId = null;
  }
}
